import Ack from "../../../classes/Ack";
import Bill from "../../../classes/Bill";
import PackingSlip from "../../../classes/PackingSlip";
import { db } from "../../../boot/firebase";
import {
  collection,
  collectionGroup,
  doc,
  deleteField,
  getDocs,
  limit,
  query,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { roundPrice } from "../../../util/misc";
import { vendorMatches, vendorOrderBase } from "../../../util/packingSlipMatch";
import { nearVendorOrders } from "../../../util/vendorOrderNumber";
import { isBoolean, isEqual, uniq } from "lodash";

export const GET_ACK = async (
  { dispatch },
  { projectID, saleID, orderID, ackID }
) => {
  return dispatch("GET_DOC", {
    converter: Ack,
    path: Ack.getPath(projectID, saleID, orderID, ackID),
    stateKey: "acks",
    id: ackID,
  });
};

export const GET_ACKS = (
  { dispatch },
  { projectID, saleID, orderID, conditions, limitCount, pagination }
) => {
  return dispatch("GET_COLLECTION", {
    converter: Ack,
    stateKey: "acks",
    path:
      projectID && saleID && orderID
        ? Ack.getPath(projectID, saleID, orderID)
        : null,
    conditions,
    limitCount,
    pagination,
  });
};

export const LISTEN_ACK = async (
  { dispatch },
  { projectID, saleID, orderID, ackID, next, error, unlisten }
) => {
  return dispatch("LISTEN_DOC", {
    converter: Ack,
    path: Ack.getPath(projectID, saleID, orderID, ackID),
    stateKey: "acks",
    id: ackID,
    next,
    error,
    unlisten,
  });
};

export const LISTEN_ACKS = (
  { dispatch },
  {
    projectID,
    saleID,
    orderID,
    conditions,
    where,
    listenerPath,
    error,
    next,
    unlisten,
  }
) => {
  const effectiveConditions = conditions ?? where;
  const path =
    projectID && saleID && orderID
      ? Ack.getPath(projectID, saleID, orderID)
      : null;
  return dispatch("LISTEN_COLLECTION", {
    converter: Ack,
    stateKey: "acks",
    path,
    colGroup: conditions || !path ? "acks" : null,
    listenerPath,
    conditions: effectiveConditions,
    next,
    error,
    unlisten,
  });
};

// What an ack tells the service issue it is for. A pending date is no date.
const serviceIssueLink = (data) =>
  data && data.serviceIssueID
    ? {
        issueID: data.serviceIssueID,
        orderNumber: data.vendorOrderNumber || "",
        expectedDate: (!data.pendingDate && data.expectedDate) || null,
      }
    : null;

export const SAVE_ACK = async (
  { dispatch, state },
  { projectID, saleID, orderID, ack, isFinalAck }
) => {
  const batch = writeBatch(db);

  const saveDocuments = (existingData) => {
    return dispatch("SAVE_DOCUMENTS", {
      batch,
      parentData: ack.data,
      fieldPath: "documents",
      folderPath: ack.path,
      existingData,
    });
  };

  const updateSale = async (changes = {}) => {
    if (isBoolean(isFinalAck)) {
      return dispatch("UPDATE_SALE_STATUS", {
        batch,
        projectID,
        saleID,
        changes: {
          ...changes,
          [`status.orders.${orderID}.fullyAcked`]: isFinalAck,
        },
      });
    }
  };

  let current = null;
  if (!ack.id) {
    // New Ack
    ack.data.created = new Date();
    ack.data.createdBy = state.user.id;

    const ref = doc(collection(db, Ack.getPath(projectID, saleID, orderID)));
    ack.id = ref.id;

    await saveDocuments();
    batch.set(ref.withConverter(Ack), ack);
    await updateSale({
      [`status.acks.${ack.id}`]: {
        orderID,
        fullyBilled: false,
        fullyReceived: false,
        // Acknowledged ship date, kept in sync by touchAck; drives the
        // pipeline year classification. A pending date is no date.
        expectedDate: ack.data.pendingDate
          ? null
          : ack.data.expectedDate || null,
      },
    });
    await batch.commit();
  } else {
    // Existing Ack
    ack.data.modified = new Date();
    ack.data.modifiedBy = state.user.id;
    current = await dispatch("GET_ACK", {
      projectID,
      saleID,
      orderID,
      ackID: ack.id,
    });

    await saveDocuments(current.data);
    batch.set(doc(db, ack.path).withConverter(Ack), ack);
    await updateSale();
    await batch.commit();
  }

  // A reorder for a service issue: the issue records the order, so receiving
  // it receives the issue's parts. Only when the link, the vendor's number or
  // the date changed -- a note or a bill on the ack is no news to the issue --
  // and an issue the ack moved off drops the order again, so a wrong pick can
  // be undone. After the commit, and on its own: the ack is saved either way,
  // so a refused issue update says so without failing the save, which a new
  // ack's dialog would retry into a second ack. The two are tried apart: the
  // ack now names the new issue, so a link skipped here would never be tried
  // again.
  const before = serviceIssueLink(current && current.data);
  const after = serviceIssueLink(ack.data);
  if (before && before.issueID !== (after && after.issueID)) {
    try {
      await dispatch("UNLINK_SERVICE_ISSUE_FROM_ORDER", {
        issueID: before.issueID,
        orderID,
        orderNumber: before.orderNumber,
      });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
  if (after && !isEqual(before, after)) {
    try {
      await dispatch("LINK_SERVICE_ISSUE_TO_ORDER", {
        ...after,
        orderID,
        projectID,
        saleID,
      });
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
  return ack;
};

export const UPDATE_ACK = (
  { state },
  { projectID, saleID, orderID, ackID, changes }
) => {
  changes.modified = new Date();
  changes.modifiedBy = state.user.id;
  return updateDoc(
    doc(db, Ack.getPath(projectID, saleID, orderID, ackID)),
    changes
  );
};

/**
 * Pure helper for `DELETE_ACK`'s cascade pre-check. Returns the i18n error
 * code (from the `error` namespace) when the cascade must be refused, or
 * `null` when it can proceed.
 *
 * Mirrors the per-doc guards in `DELETE_BILL` / `DELETE_PACKING_SLIP`:
 *   - a child bill with any payment applied (`balance !== totalCost`) blocks
 *     because deleting it would orphan the locked `tx` payment records and
 *     double-count the vendor balance.
 *   - a child packing slip with `qcInspected: true` blocks because its QC
 *     pass wrote per-item / sale-level state that this cascade doesn't
 *     reverse.
 *
 * Exported so it can be unit-tested without spinning up Firestore mocks.
 *
 * @param {Array<object>} billsData - raw `bill.data()` objects for the ack.
 * @param {Array<object>} packingSlipsData - raw `packingSlip.data()` objects.
 * @returns {"ackHasFinalizedChildren"|"ackHasPaidBills"|"ackHasQcdPackingSlips"|null}
 */
export const checkAckCascadeBlocked = (billsData, packingSlipsData) => {
  const hasPaidBill = (billsData || []).some((data) => {
    const totalCost = Number(data && data.totalCost) || 0;
    const balance = Number(data && data.balance) || 0;
    return roundPrice(Math.abs(totalCost - balance)) > 0;
  });
  const hasQcdPackingSlip = (packingSlipsData || []).some(
    (data) => data && data.qcInspected === true
  );
  if (hasPaidBill && hasQcdPackingSlip) {
    return "ackHasFinalizedChildren";
  }
  if (hasPaidBill) {
    return "ackHasPaidBills";
  }
  if (hasQcdPackingSlip) {
    return "ackHasQcdPackingSlips";
  }
  return null;
};

export const DELETE_ACK = async (
  { dispatch },
  { projectID, saleID, orderID, ackID }
) => {
  const batch = writeBatch(db);
  batch.delete(doc(db, Ack.getPath(projectID, saleID, orderID, ackID)));

  // Cascade-delete bills and packing-slips that reference this ack so they
  // don't linger as orphans. Without this, subsequent SAVE_BILL /
  // SAVE_PACKING_SLIP / DELETE_BILL / DELETE_PACKING_SLIP calls would write
  // dot-paths like `status.acks.<deletedAckID>.fullyBilled`, which Firestore
  // interprets as field-create and recreates a malformed `status.acks` entry
  // (no `orderID`, no `fullyReceived`) — pinning the sale at "production"
  // because `computeSaleStatus` then sees an unreceived ack. Sale 03900066B
  // reproduced this. Pattern mirrors DELETE_ORDER's cascade.
  const [billsSnap, packingSlipsSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, Bill.getPath(projectID, saleID, orderID)),
        where("ackID", "==", ackID)
      )
    ),
    getDocs(
      query(
        collection(db, PackingSlip.getPath(projectID, saleID, orderID)),
        where("ackID", "==", ackID)
      )
    ),
  ]);

  // Refuse the cascade if any child bill has been (partially) paid or
  // any child packing slip has been QC-inspected. Mirrors the per-doc
  // guards in DELETE_BILL / DELETE_PACKING_SLIP — those finalizing
  // operations have side-effects (locked vendor `tx` records, per-item
  // `quantityReady` writes, sale-level counter bumps) that the cascade
  // does not reverse. The user is told which child blocks the delete so
  // they can resolve it first.
  const blockedReason = checkAckCascadeBlocked(
    billsSnap.docs.map((d) => d.data()),
    packingSlipsSnap.docs.map((d) => d.data())
  );
  if (blockedReason) {
    throw new Error(blockedReason);
  }

  const changes = {
    [`status.acks.${ackID}`]: deleteField(),
    [`status.orders.${orderID}.fullyAcked`]: false,
  };
  billsSnap.forEach((d) => {
    batch.delete(d.ref);
    changes[`status.bills.${d.id}`] = deleteField();
  });
  packingSlipsSnap.forEach((d) => {
    batch.delete(d.ref);
    changes[`status.packingSlips.${d.id}`] = deleteField();
  });

  // The service issue the ack recorded its order on (SAVE_ACK), read while
  // the ack is still there to say.
  let ack = null;
  try {
    ack = await dispatch("GET_ACK", { projectID, saleID, orderID, ackID });
  } catch (error) {
    // Already gone: nothing to read.
  }
  const link = serviceIssueLink(ack && ack.data);

  await dispatch("UPDATE_SALE_STATUS", {
    batch,
    projectID,
    saleID,
    changes,
  });
  await batch.commit();

  // The issue drops the order, as when the ack moves off it, unless another
  // ack on the order is still for the issue (a revised ack filed beside this
  // one). After the commit, and on its own, like SAVE_ACK's.
  if (link) {
    try {
      const others = await getDocs(
        query(
          collection(db, Ack.getPath(projectID, saleID, orderID)),
          where("serviceIssueID", "==", link.issueID)
        )
      );
      if (!others.docs.some((d) => d.id !== ackID)) {
        await dispatch("UNLINK_SERVICE_ISSUE_FROM_ORDER", {
          issueID: link.issueID,
          orderID,
          orderNumber: link.orderNumber,
        });
      }
    } catch (error) {
      dispatch("NOTIFY_ERROR", error);
    }
  }
};

/**
 * The orders whose acknowledgement carries a vendor order number, each with
 * that ack. A packing slip's vendor order number is printed by the vendor's
 * own system and is the most reliable key it carries (see
 * util/packingSlipMatch.js).
 *
 * A prefix range on the number's base over the acks collection group
 * (indexed for this in firestore.indexes.json): Quaker prints "D50071 01" for
 * release 01 of the ack's "D50071 00", so the base is what is compared, and
 * an in-memory check keeps only true base matches so "D505" cannot pull in
 * "D5051". Tried as typed and upper-cased, since the ack's number was entered
 * by hand. A failed query (index not built yet, offline) yields [] and the
 * caller falls back to the PO.
 */
export const SEARCH_ACKS_BY_VENDOR_ORDER = async (
  { dispatch },
  { vendorOrderNumber }
) => {
  const base = vendorOrderBase(vendorOrderNumber);
  if (!base) return [];
  const typed = String(vendorOrderNumber)
    .trim()
    .replace(/\s+\d{2}$/, "");
  const hits = [];
  const seen = new Set();
  for (const prefix of uniq([base, typed])) {
    const end =
      prefix.slice(0, -1) +
      String.fromCharCode(prefix.charCodeAt(prefix.length - 1) + 1);
    let snapshot;
    try {
      snapshot = await getDocs(
        query(
          collectionGroup(db, "acks"),
          where("vendorOrderNumber", ">=", prefix),
          where("vendorOrderNumber", "<", end),
          limit(20)
        )
      );
    } catch (err) {
      console.warn("Ack search query failed:", err.message);
      continue;
    }
    for (const ackDoc of snapshot.docs) {
      if (seen.has(ackDoc.ref.path)) continue;
      seen.add(ackDoc.ref.path);
      const data = ackDoc.data();
      if (vendorOrderBase(data.vendorOrderNumber) !== base) continue;
      // projects/{projectID}/sales/{saleID}/orders/{orderID}/acks/{ackID}
      const [, projectID, , saleID, , orderID] = ackDoc.ref.path.split("/");
      const order = await dispatch("GET_ORDER", {
        projectID,
        saleID,
        orderID,
      }).catch(() => null);
      if (!order) continue;
      hits.push({
        order,
        ack: {
          id: ackDoc.id,
          vendorOrderNumber: data.vendorOrderNumber || "",
          fullyReceived: !!data.fullyReceived,
          serviceIssueID: data.serviceIssueID || "",
        },
      });
    }
  }
  return hits;
};

/**
 * How many orders one vendor has on a sale, and the vendor order numbers
 * (by base, so releases count once) their acks carry. A Quaker carton's line
 * is only our quote line while the sale's items of that vendor came on one
 * sales order (see itemOnVendorLine).
 */
export const GET_SALE_VENDOR_ORDER_NUMBERS = async (
  { dispatch },
  { projectID, saleID, vendor }
) => {
  const orders = (
    (await dispatch("GET_ORDERS", { projectID, saleID })) || []
  ).filter((order) => order && order.data.vendor === vendor);
  const numbers = new Set();
  for (const order of orders) {
    const acks =
      (await dispatch("GET_ACKS", { projectID, saleID, orderID: order.id })) ||
      [];
    acks.forEach((ack) => {
      const base = vendorOrderBase(ack && ack.data.vendorOrderNumber);
      if (base) numbers.add(base);
    });
  }
  return { orderCount: orders.length, orderNumbers: [...numbers] };
};

/**
 * The open acks whose vendor order number is a near miss of the given one:
 * a zero too many or too few, or one character off (util/vendorOrderNumber).
 *
 * A near miss cannot be found by a range query -- "DMC000247", "DMC0247" and
 * "DNC00247" share no prefix -- so every open ack is read once and compared
 * in memory, the way the poller does it. A few hundred documents, and a
 * batch of slips reuses one read: the fetch is memoised for a minute.
 *
 * `vendorName` is the vendor printed on the slip; when given, only acks of
 * a vendor that shares its distinguishing word are considered, so a Marvin
 * slip is never offered a Quaker ack.
 *
 * @returns {Array<{ order, ack, reason }>} best first, at most `max`
 */
let openAcksCache = { at: 0, promise: null };
const OPEN_ACKS_TTL_MS = 60 * 1000;

const readOpenAcks = () => {
  const now = Date.now();
  if (!openAcksCache.promise || now - openAcksCache.at > OPEN_ACKS_TTL_MS) {
    openAcksCache = {
      at: now,
      promise: getDocs(
        query(collectionGroup(db, "acks"), where("fullyReceived", "==", false))
      )
        .then((snapshot) => snapshot.docs)
        .catch((err) => {
          console.warn("Open ack read failed:", err.message);
          openAcksCache = { at: 0, promise: null };
          return [];
        }),
    };
  }
  return openAcksCache.promise;
};
export const resetOpenAcksCache = () => {
  openAcksCache = { at: 0, promise: null };
};

export const SEARCH_ACKS_NEAR_VENDOR_ORDER = async (
  { dispatch },
  { vendorOrderNumber, vendorName = "", max = 5 }
) => {
  const target = String(vendorOrderNumber || "").trim();
  if (!target) return [];
  const docs = await readOpenAcks();
  const candidates = docs
    .map((ackDoc) => ({ ackDoc, data: ackDoc.data() }))
    .filter(
      ({ data }) =>
        !vendorName || !data.vendor || vendorMatches(vendorName, data.vendor)
    )
    .map((entry) => ({
      ...entry,
      vendorOrderNumber: entry.data.vendorOrderNumber,
    }));

  const hits = [];
  for (const { candidate, reason } of nearVendorOrders(target, candidates)) {
    // An exact hit belongs to SEARCH_ACKS_BY_VENDOR_ORDER; this is the near ones.
    if (reason === "exact") continue;
    if (hits.length >= max) break;
    // projects/{projectID}/sales/{saleID}/orders/{orderID}/acks/{ackID}
    const [, projectID, , saleID, , orderID] =
      candidate.ackDoc.ref.path.split("/");
    const order = await dispatch("GET_ORDER", {
      projectID,
      saleID,
      orderID,
    }).catch(() => null);
    if (!order) continue;
    hits.push({
      order,
      ack: {
        id: candidate.ackDoc.id,
        projectID,
        saleID,
        orderID,
        vendorOrderNumber: candidate.data.vendorOrderNumber || "",
        fullyReceived: !!candidate.data.fullyReceived,
      },
      reason,
    });
  }
  return hits;
};
